import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { formatRelative } from '~/utils/date';

import { useRunLabels } from '../hooks/use-run-labels';
import type { ContentProgress, RunProgress } from '../types/run.api.types';
import { shortId } from '../utils/product-master-format';
import { formatCount, humanizeCode, percentWidth } from '../utils/run-format';

/** One row of the IDs popover: a label, the value (short, monospace) and a copy button. */
function IdRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1">
        <span className="text-foreground truncate font-mono">{shortId(value)}</span>
        <CopyIconButton text={value} label={value} className="shrink-0" />
      </span>
    </div>
  );
}

/** A plain (non-copyable) fact row — "Sent to", a human label, never an id. */
function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground min-w-0 truncate text-right">{value}</span>
    </div>
  );
}

/** The stage attempt whose hand-off ids the popover shows, and why. */
function idsSource(
  progress: RunProgress | undefined,
  activeTab: string,
): { stage: string | null; refIds: Record<string, string[]> | null | undefined } {
  const pendingGate = progress?.pendingGate ?? null;
  const stages = progress?.stages ?? [];
  // On the open gate's own screen (Products, where its decision panel sits, or Summary for the
  // APPROVAL gate, whose decision band replaced the Publishing tab): this attempt's own ids, not
  // whatever the batch last handed off.
  if (
    pendingGate &&
    (activeTab === 'products' || (activeTab === 'overview' && pendingGate.stage === 'APPROVAL'))
  ) {
    const stage = stages.find((s) => s.stage === pendingGate.stage);
    return { stage: pendingGate.stage, refIds: stage?.refIds };
  }
  // Otherwise: the batch's latest stage that actually carries a hand-off (most stages don't).
  const latest = [...stages]
    .reverse()
    .find((s) => Object.values(s.refIds ?? {}).some((ids) => ids.length > 0));
  return { stage: latest?.stage ?? null, refIds: latest?.refIds };
}

interface HeaderIdsPopoverProps {
  runId: string;
  progress: RunProgress | undefined;
  /** The run shell's active tab (`RunHeader`'s own prop) — see `idsSource`. */
  activeTab: string;
}

/**
 * Header "⧉ IDs" popover (design round 2 §2A/§M-A: replaces the collapsed DETAILS section every
 * gate body used to end on, moved into the header's meta-chip row instead). Click, not hover —
 * these are ids worth copying, not a glance fact. Rows: the batch id, which stage this attempt
 * was sent to (a human label via `stageLabel`, never a raw code), the content-generation job id
 * (`refIds.batchId` — distinct from the batch id above; the two used to share the same "Batch id"
 * label), and any other hand-off ids that stage carries.
 */
export function HeaderIdsPopover({ runId, progress, activeTab }: HeaderIdsPopoverProps) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { stageLabel } = useRunLabels();
  const { stage, refIds } = idsSource(progress, activeTab);
  const jobId = refIds?.batchId?.[0] ?? null;
  const otherEntries = Object.entries(refIds ?? {}).filter(
    ([kind, ids]) => kind !== 'batchId' && ids.length > 0,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-muted/40 text-muted-foreground h-auto cursor-pointer gap-1.5 rounded-full border px-2.5 py-1 text-xs font-normal whitespace-nowrap"
        >
          <Copy aria-hidden="true" className="size-3.5 shrink-0" />
          {t('header.ids.chip')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-2 p-3 text-xs">
        <IdRow label={t('header.ids.batch')} value={runId} />
        {stage ? <FactRow label={t('header.ids.sentTo')} value={stageLabel(stage)} /> : null}
        {jobId ? <IdRow label={t('header.ids.contentGenJob')} value={jobId} /> : null}
        {otherEntries.map(([kind, ids]) =>
          ids.map((id) => (
            <IdRow
              key={`${kind}-${id}`}
              label={t(`handoffIds.${kind}`, { defaultValue: humanizeCode(kind) })}
              value={id}
            />
          )),
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ContentProgressProps {
  /** `StageProgress.contentProgress`; null/undefined renders nothing (no ping received yet). */
  progress: ContentProgress | null | undefined;
}

/** Live-only statuses — once the ping says COMPLETED/FAILED/CANCELLED the stage's own completion
 *  (counts, status) is the real answer, so the ping is no longer shown (Display only, per field). */
const LIVE_STATUSES = new Set(['STARTED', 'IN_PROGRESS']);

/**
 * Finding 9 — content-generation's live progress ping for a running content stage, never shown
 * before now: step, `done/expected` products and a percent bar when the server sends one, "last
 * update" relative to our own clock (`receivedAt`). Renders nothing once the stage has completed
 * (its own counts take over) or before the first ping arrives.
 */
export function GateContentProgress({ progress }: ContentProgressProps) {
  const { t } = useTranslation('catalog-enrichment-run');
  if (!progress || !LIVE_STATUSES.has(progress.status)) return null;
  const done = progress.productsDone ?? null;
  const expected = progress.productsExpected ?? null;
  const failed = progress.productsFailed ?? null;
  return (
    <div className="bg-muted/40 flex flex-col gap-1.5 rounded-lg border border-dashed p-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="text-foreground font-medium">
          {progress.step
            ? t('contentProgress.step', { step: progress.step })
            : t('contentProgress.running')}
        </span>
        <span className="text-muted-foreground">
          {t('contentProgress.updated', { when: formatRelative(progress.receivedAt) })}
        </span>
      </div>
      {expected !== null ? (
        <>
          <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full" aria-hidden="true">
            <span
              className="bg-info block h-full"
              style={{
                width:
                  typeof progress.percentComplete === 'number'
                    ? `${String(Math.min(100, Math.max(0, progress.percentComplete)))}%`
                    : percentWidth(done ?? 0, expected),
              }}
            />
          </div>
          <span className="text-muted-foreground">
            {t('contentProgress.doneOf', {
              done: formatCount(done ?? 0),
              total: formatCount(expected),
            })}
            {failed ? ` · ${t('contentProgress.failed', { count: failed })}` : ''}
          </span>
        </>
      ) : null}
    </div>
  );
}
